import { create } from 'zustand';
import type { RedirectBulkResultDto, RedirectDto, RedirectRequest } from '@ecom/contracts';
import { adminApi, errorMessage, withSession } from '@/lib/api-client';

/** Old links that keep working (ADR-073) — searched and paged on the server. */

interface RedirectsState {
  items: RedirectDto[];
  nextCursor: string | null;
  total: number | null;
  search: string;
  loading: boolean;
  saving: boolean;
  error: string | null;
  saveError: string | null;
  /** The last paste's outcome, line by line. */
  bulkResult: RedirectBulkResultDto | null;

  load: (search?: string) => Promise<void>;
  loadMore: () => Promise<void>;
  save: (id: string | null, input: RedirectRequest) => Promise<boolean>;
  remove: (id: string) => Promise<boolean>;
  bulk: (text: string, permanent: boolean) => Promise<boolean>;
  clearSaveError: () => void;
}

const PAGE_SIZE = 20;

export const useRedirectsStore = create<RedirectsState>((set, get) => ({
  items: [],
  nextCursor: null,
  total: null,
  search: '',
  loading: false,
  saving: false,
  error: null,
  saveError: null,
  bulkResult: null,

  async load(search) {
    const term = search ?? get().search;
    set({ loading: true, error: null, search: term });
    try {
      const page = await withSession(() => adminApi.redirects.list({ limit: PAGE_SIZE, ...(term.trim() ? { search: term.trim() } : {}) }));
      if (get().search !== term) return;
      set({ items: page.items, nextCursor: page.meta.nextCursor, total: page.meta.total ?? null, loading: false });
    } catch (error) {
      set({ loading: false, error: errorMessage(error, 'Could not load the redirects.') });
    }
  },

  async loadMore() {
    const { nextCursor, search, loading } = get();
    if (!nextCursor || loading) return;
    set({ loading: true });
    try {
      const page = await withSession(() =>
        adminApi.redirects.list({ limit: PAGE_SIZE, cursor: nextCursor, ...(search.trim() ? { search: search.trim() } : {}) }),
      );
      set((state) => ({ items: [...state.items, ...page.items], nextCursor: page.meta.nextCursor, loading: false }));
    } catch (error) {
      set({ loading: false, error: errorMessage(error, 'Could not load more redirects.') });
    }
  },

  async save(id, input) {
    set({ saving: true, saveError: null });
    try {
      const saved = await withSession(() => (id ? adminApi.redirects.update(id, input) : adminApi.redirects.create(input)));
      set((state) => ({
        saving: false,
        items: id ? state.items.map((item) => (item.id === id ? saved : item)) : [saved, ...state.items],
        total: id || state.total === null ? state.total : state.total + 1,
      }));
      return true;
    } catch (error) {
      set({ saving: false, saveError: errorMessage(error, 'Could not save the redirect.') });
      return false;
    }
  },

  async remove(id) {
    set({ saving: true, error: null });
    try {
      await withSession(() => adminApi.redirects.remove(id));
      set((state) => ({ saving: false, items: state.items.filter((item) => item.id !== id), total: state.total === null ? null : state.total - 1 }));
      return true;
    } catch (error) {
      set({ saving: false, error: errorMessage(error, 'Could not delete the redirect.') });
      return false;
    }
  },

  async bulk(text, permanent) {
    set({ saving: true, saveError: null, bulkResult: null });
    try {
      const result = await withSession(() => adminApi.redirects.bulk({ text, permanent }));
      set({ saving: false, bulkResult: result });
      await get().load();
      return true;
    } catch (error) {
      set({ saving: false, saveError: errorMessage(error, 'Could not add the redirects.') });
      return false;
    }
  },

  clearSaveError() {
    set({ saveError: null, bulkResult: null });
  },
}));
